import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { fetchSupplierOrders } from '../api/marketplaceApi';

const SupplierQuotations: React.FC = () => {
  const [quotations, setQuotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Quotations are derived from RFQ responses - fetch from orders that came from quotations
      const response = await fetchSupplierOrders({ limit: 50 });
      const items = response.data?.items || response.data || [];
      // Filter to only show orders that originated from quotations
      setQuotations(items.filter((o: any) => o.rfq_id || o.quotation_id));
    } catch (err: any) {
      setError(err.message || 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (n: number) => new Intl.NumberFormat('en-RW', { style: 'currency', currency: 'RWF', minimumFractionDigits: 0 }).format(n || 0);

  const statusConfig: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
    draft: { bg: 'bg-gray-100 border-gray-200', text: 'text-gray-600', icon: DocumentTextIcon },
    submitted: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: ClockIcon },
    accepted: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', icon: CheckCircleIcon },
    rejected: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: ExclamationTriangleIcon },
    expired: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: ClockIcon },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 rounded-2xl p-8 text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -translate-y-1/2 translate-x-1/4"></div>
        </div>
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <ClipboardDocumentListIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">My Quotations</h1>
              <p className="text-emerald-100 mt-1">Track your submitted quotations and their status</p>
            </div>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 font-medium transition-all">
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-emerald-200 rounded-full"></div>
            <div className="w-12 h-12 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto" />
          <p className="text-red-700 font-semibold mt-3">{error}</p>
          <button onClick={fetchData} className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium">Retry</button>
        </div>
      ) : quotations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center mx-auto">
            <ClipboardDocumentListIcon className="w-10 h-10 text-emerald-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mt-4">No quotations yet</h3>
          <p className="text-gray-500 mt-2 max-w-md mx-auto">Submit quotations on RFQs from the <strong>RFQ Opportunities</strong> page to see them here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quotations.map((q: any) => {
            const st = statusConfig[q.quotation_status || q.status] || statusConfig.submitted;
            const StatusIcon = st.icon;
            return (
              <div key={q.id} className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center shadow-md">
                      <ClipboardDocumentListIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider">
                        {q.quotation_number || q.order_number || `Q-${q.id}`}
                      </p>
                      <h3 className="text-lg font-bold text-gray-900 mt-0.5">
                        {q.rfq_title || q.title || `RFQ-${q.rfq_id || 'N/A'}`}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Buyer: {q.buyer_name || q.buyer_org_name || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${st.bg} ${st.text}`}>
                    <StatusIcon className="w-4 h-4" />
                    {(q.quotation_status || q.status || 'submitted').replace('_', ' ')}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 rounded-xl text-center">
                    <p className="text-xs font-bold text-gray-400 uppercase">Amount</p>
                    <p className="text-lg font-extrabold text-gray-900 mt-1">{fmt(q.total_amount || q.quoted_amount || 0)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl text-center">
                    <p className="text-xs font-bold text-gray-400 uppercase">Items</p>
                    <p className="text-lg font-extrabold text-gray-900 mt-1">{q.item_count || q.items?.length || 0}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl text-center">
                    <p className="text-xs font-bold text-gray-400 uppercase">Date</p>
                    <p className="text-sm font-semibold text-gray-700 mt-2">{q.created_at ? new Date(q.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>

                {q.delivery_terms && (
                  <div className="mt-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                    <p className="text-xs font-bold text-emerald-600 uppercase mb-1">Delivery Terms</p>
                    <p className="text-sm text-emerald-700">{q.delivery_terms}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SupplierQuotations;
